import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';

// --- DATA ---
const projects = [
  {
    id: 100,
    title: "TM International Construction - Virginia",
    category: "Commercial & Residential",
    location: "Virginia, USA",
    year: "Ongoing",
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=2070&auto=format&fit=crop",
    description: "Our flagship Virginia operation provides comprehensive construction services for residential and commercial clients across the Commonwealth. With full state licensure and certification, we deliver quality workmanship across all construction disciplines including new construction, renovations, remodeling, and infrastructure development. This division has completed over 300 projects with values ranging from $100,000 to $45 million.",
    stats: {
      client: "Various Private/Commercial",
      area: "300+ Projects",
      status: "Active",
      services: "New Construction, Renovation"
    },
    quote: "Delivering quality workmanship across all construction disciplines with values ranging from 100,000 to 45 million."
  },
  {
    id: 101,
    title: "TM International Construction - Washington DC",
    category: "Government & Federal",
    location: "Washington DC",
    year: "2024",
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop",
    description: "Specializing in federal and government projects, our DC division brings expertise in navigating complex regulatory requirements while delivering exceptional results for government facilities, office buildings, and commercial properties. We maintain security clearances and have established relationships with key government agencies and prime contractors. Our DC portfolio includes projects valued between $5 million and $35 million.",
    stats: {
      client: "Federal Government",
      area: "Various Locations",
      status: "In Progress",
      services: "Government Facilities, Security"
    },
    quote: "Expertise in navigating complex regulatory requirements for projects valued between 5 million and 35 million."
  },
  {
    id: 102,
    title: "TM International Construction - Maryland",
    category: "Sustainable Development",
    location: "Maryland, USA",
    year: "2023",
    image: "https://images.unsplash.com/photo-1518780664697-55e3ad937233?q=80&w=1965&auto=format&fit=crop",
    description: "Our Maryland Sustainable Development Division focuses on environmentally responsible construction practices, green building certifications, and energy-efficient solutions for residential and commercial clients. We specialize in sustainable retrofits, net-zero energy buildings, and developments that minimize environmental impact while maximizing long-term value. This division has achieved over 25 million in completed green building projects.",
    stats: {
      client: "Eco-Developers",
      // area: "$25M+ Value",
      status: "Completed",
      services: "LEED Certification, Retrofits"
    },
    quote: "Since its inception, the Maryland Sustainable Development Division has completed numerous green building projects, focusing on net-zero energy solutions and sustainable retrofits."
  },
  {
    id: 103,
    title: "TM International Real Estate",
    category: "Property Development",
    location: "Multiple Markets",
    year: "Ongoing",
    image: "https://images.unsplash.com/photo-1460317442991-0ec209397118?q=80&w=2070&auto=format&fit=crop",
    description: "Providing investment and consulting services, our Delaware entity focuses on strategic partnerships, project financing, and business development opportunities in the construction sector. This division manages our investment portfolio, identifies acquisition targets, and provides strategic advisory services to construction companies seeking growth capital or operational improvements.",
    stats: {
      client: "Investors / Partners",
      area: "Mixed-Use",
      status: "Active",
      services: "Acquisitions, Asset Management"
    },
    quote: "Identifying and developing high-value properties including residential developments and mixed-use projects."
  },
  {
    id: 104,
    title: "TM International Real Estate",
    category: "Property Development",
    location: "Multiple Markets",
    year: "Ongoing",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070&auto=format&fit=crop",
    description: "Our real estate division specializes in property development, investment opportunities, and asset management. We identify and develop high-value properties that generate strong returns for our investors through strategic acquisitions, value-add improvements, and professional property management. Our real estate portfolio includes residential developments, mixed-use projects, and commercial properties across multiple markets.",
    stats: {
      client: "Investors / Partners",
      area: "Mixed-Use",
      status: "Active",
      services: "Acquisitions, Asset Management"
    },
    quote: "Providing strategic advisory services to construction companies seeking growth capital or operational improvements."
  },
  {
    id: 105,
    title: "AskMYteam.com",
    category: "Digital Platform",
    location: "Global / Online",
    year: "Live",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop",
    description: "Our innovative digital platform connects homeowners and businesses with qualified construction professionals through advanced matching algorithms and comprehensive vetting processes. This technology-driven solution streamlines the contractor selection process, provides transparent pricing, and ensures quality service delivery through customer reviews and performance monitoring. The platform has facilitated over 50 million in construction projects since launch.",
    stats: {
      client: "Homeowners/Businesses",
      // area: "$50M+ Facilitated",
      status: "Live Platform",
      services: "Contractor Matching, Vetting"
    },
    quote: "Since its launch, the platform has supported a large volume of construction projects across multiple categories and markets."
  }
];

// --- COMPONENTS ---

// Portfolio Page Component
const PortfolioPage = () => {
  const navigate = useNavigate();

  return (
    <div className="animate-fade-in bg-white min-h-screen flex flex-col" style={{ fontFamily: "Monstserrat, proxima-nova, Helveti, sans-serif" }}>
      {/* Header Section */}
      <div className="max-w-[1200px] mx-auto mt-20 text-center">
        <AnimatedSection animationType="fadeUp">
          <h2 className="text-4xl md:text-5xl font-semibold uppercase tracking-tight text-black mb-6">
            Portfolio <span className="text-slate-300 font-light">&</span> Companies
          </h2>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: 200 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-1 bg-[#2a3890] mx-auto rounded-full mt-2"
          />
          {/* <div className="w-48 h-1 bg-[#2a3890] mx-auto rounded-full mb-8"></div> */}

          <p className="text-lg md:text-xl text-slate-600 font-light max-w-4xl mx-auto leading-relaxed mt-4">
            TM International Group operates through multiple specialized companies, each serving distinct market
            segments while maintaining our commitment to excellence.
          </p>
        </AnimatedSection>
      </div>

      {/* 2x2 Grid Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex-grow ml-10 mr-10" style={{ alignSelf: 'center' }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map((project) => (
            <div
              key={project.id}
              onClick={() => navigate(`/projects/${project.id}`)}
              className="group relative cursor-pointer overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 h-[500px]"
            >
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end p-8">
                <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <span className="text-white text-xs font-bold tracking-widest uppercase mb-2 block">
                    {project.location}
                  </span>
                  <h3 className="text-2xl font-display text-white font-medium uppercase leading-tight mb-3">
                    {project.title}
                  </h3>
                  <p className="text-gray-300 text-sm line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100 mb-4">
                    {project.description}
                  </p>
                  <div className="inline-flex items-center text-white text-sm font-medium border-b border-transparent group-hover:border-[#2a3890] pb-1 transition-all duration-300">
                    View Details <i className="fas fa-arrow-right ml-2 text-white"></i>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AnimatedSection animationType="fadeUp">
        <div className="bg-gray-900 max-w-full mx-auto px-4 text-center py-12 text-white">
          <h2 className="text-2xl font-display uppercase tracking-widest mb-4">Ready to build your vision?</h2>
          <button className="border border-white px-8 py-3 hover:bg-white hover:text-black transition uppercase text-sm tracking-wider"
            onClick={() => window.location.href = '/contact'}>
            Contact Us
          </button>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default PortfolioPage;